import type { Action, PublicId } from 'flow/store/engine/types';
import { generateOperations } from '../../changelog';
import { executePredicatedProcessors } from '../../lib/predicatedProcessor';
import createOperationProcessors, { createChangesByOperationBuffer } from '../../operationProcessors';
import { Operation } from '../../types';
import type {
  ChangesByOperation,
  RFC6902Operation,
  ChangelogInputBundle,
} from '../../types';
import mergeDiffFormatter from './mergeDiffFormatter';
import type { ActionFieldDiff, DiffArrayPath, MergeDiffResult } from './types';
import { convertOpToActionModifiedOp, isActionRuleGroupingModifiedChange } from '../../guards';
import { areEqual } from 'flow/service/util';
import {
  createChangelogFromAction,
  extractActionFieldSegments,
  findArrayAncestor,
  getAt,
  getItemIdentity,
  readOp,
} from './helpers';
import isPlainObject from 'lodash/isPlainObject';

/**
 * Processes a list of operations and creates Changes representing them.
 * @param operations operations that happened from versionA to versionB
 * @param versions a bundle of versions to compare
 * @returns a record of changes keyed by operation type.
 */
function computeChangesPerOperation(args: {
  A: ChangelogInputBundle;
  B: ChangelogInputBundle;
  operations: RFC6902Operation[];
}): ChangesByOperation {
  const { operations, A, B } = args;
  const changesBuffer = createChangesByOperationBuffer();
  const operationProcessors = createOperationProcessors({ changesBuffer, A, B });
  executePredicatedProcessors({ items: operations, processorRegistry: operationProcessors });
  return changesBuffer.package();
}

export default function generateMergeDiff(args: {
  A: ChangelogInputBundle;
  B: ChangelogInputBundle;
  isDiffOrLinkedSnippetUpgrade?: boolean;
}): MergeDiffResult {
  const { A, B, isDiffOrLinkedSnippetUpgrade = false } = args;
  const operations = generateOperations({ A, B });

  const {
    add: addedChanges,
    modify: modifiedChanges,
    remove: removedChanges,
  } = computeChangesPerOperation({ operations, A, B });

  // Special Case:
  // we move the modified action rule to be handled by the added formatter
  // since we will only be adding rules at this time
  const ruleModifiedOps = modifiedChanges.filter(change => (
    isActionRuleGroupingModifiedChange(change)
  ));
  const adjustedAddedChanges = [...addedChanges, ...ruleModifiedOps];

  // Special case if upgrading a linked snippet or in diff-view:
  // Any non-action changes that do not correspond to an 'added' or 'removed' action are treated
  // as modified action changes, since they are modifying an action that exists on both sides.
  // This includes removals (e.g. deleting a threshold): the owning action survives and is
  // therefore modified, and nothing else marks it
  const adjustedModifiedChanges = [...modifiedChanges];
  if (isDiffOrLinkedSnippetUpgrade) {
    const addedActionPublicIdSet = new Set<PublicId>();
    for (const change of addedChanges) {
      if (change.descriptor === 'action') {
        addedActionPublicIdSet.add(change.actionPublicId);
      }
    }

    const removedActionPublicIdSet = new Set<PublicId>();
    for (const change of removedChanges) {
      if (change.descriptor === 'action') {
        removedActionPublicIdSet.add(change.actionPublicId);
      }
    }

    for (const change of [...addedChanges, ...modifiedChanges, ...removedChanges]) {
      if (
        change.descriptor !== 'action'
        && !addedActionPublicIdSet.has(change.actionPublicId)
        && !removedActionPublicIdSet.has(change.actionPublicId)
      ) {
        adjustedModifiedChanges.push(convertOpToActionModifiedOp(change));
      }
    }
  }

  return {
    added: new Set<PublicId>(
      mergeDiffFormatter({
        A,
        B,
        changes: adjustedAddedChanges,
        formattedOperation: Operation.Add,
      }),
    ),
    modified: new Set<PublicId>(
      mergeDiffFormatter({
        A,
        B,
        changes: adjustedModifiedChanges,
        formattedOperation: Operation.Modify,
      }),
    ),
    removed: new Set<PublicId>(
      mergeDiffFormatter({
        A,
        B,
        changes: removedChanges,
        formattedOperation: Operation.Remove,
      }),
    ),
  };
}

/** For diffMode, actionA -> initialAction & actionB -> currentAction */
export function generateFocusedActionDiff(args: {
  actionA: Action;
  actionB: Action;
}): ActionFieldDiff {
  const { actionA, actionB } = args;
  const diffField: ActionFieldDiff = {};

  const A = createChangelogFromAction(actionA);
  const B = createChangelogFromAction(actionB);

  const operations = generateOperations({ A, B });

  /**
   * Diffs two versions of an array by item identity and writes a status per changed item into
   * `diffField`, keyed as `arrayPath[identity]`.
   */
  function diffArrays<T>(arrayA: T[], arrayB: T[], arrayPathObject: DiffArrayPath) {
    const { arrayPath } = arrayPathObject;
    const identityOf = (item: T) => getItemIdentity(item, arrayPath);

    // No registered identity for this array's item shape: skip per-item checks
    //
    const sampleItem = arrayA[0] ?? arrayB[0];
    if (sampleItem !== undefined && identityOf(sampleItem) === null) return;

    // Map the arrays for non-positional diffing
    const itemsInA = new Map(arrayA.map(item => [identityOf(item), item]));
    const itemsInB = new Map(arrayB.map(item => [identityOf(item), item]));

    // Assign diffStatus values
    itemsInA.forEach((itemA, identity) => {
      if (!itemsInB.has(identity)) {
        diffField[`${arrayPath}[${identity}]`] = Operation.Remove;
        return;
      }
      if (!areEqual(itemA, itemsInB.get(identity))) {
        diffField[`${arrayPath}[${identity}]`] = Operation.Modify;
      }
    });

    itemsInB.forEach((_itemB, identity) => {
      if (!itemsInA.has(identity)) {
        diffField[`${arrayPath}[${identity}]`] = Operation.Add;
      }
    });
  }

  /**
   * Diffs the children between two possible objects. If the parent is added or removed, cascade
   * that same diffStatus to the children for lookup in the consumer. If the parent is modified,
   * do a meaningful diff between the keys
   */
  function expandDiff(expandArgs: {
    valueA: any;
    valueB: any;
    diffStatus: Operation;
    path: string;
  }) {
    const {
      valueA, valueB, diffStatus, path,
    } = expandArgs;

    // Arrays are handled by identity in diffArrays, not by key expansion.
    if (Array.isArray(valueA) || Array.isArray(valueB)) return;

    // Parent added or removed: every descendant inherits that status.
    if (diffStatus !== Operation.Modify) {
      const source = isPlainObject(valueB) ? valueB : valueA;
      if (!isPlainObject(source)) return;

      for (const key of Object.keys(source)) {
        const childPath = `${path}/${key}`;
        diffField[childPath] = diffStatus;
        expandDiff({
          valueA: isPlainObject(valueA) ? valueA[key] : undefined,
          valueB: isPlainObject(valueB) ? valueB[key] : undefined,
          diffStatus,
          path: childPath,
        });
      }
      return;
    }

    // Parent modified: resolve each child on its own.
    if (!isPlainObject(valueA) && !isPlainObject(valueB)) return;

    const keys = new Set([
      ...(isPlainObject(valueA) ? Object.keys(valueA) : []),
      ...(isPlainObject(valueB) ? Object.keys(valueB) : []),
    ]);

    for (const key of keys) {
      const childA = isPlainObject(valueA) ? valueA[key] : undefined;
      const childB = isPlainObject(valueB) ? valueB[key] : undefined;

      if (!areEqual(childA, childB)) {
        let childStatus: Operation;
        if (childA === undefined) childStatus = Operation.Add;
        else if (childB === undefined) childStatus = Operation.Remove;
        else childStatus = Operation.Modify;

        const childPath = `${path}/${key}`;
        diffField[childPath] = childStatus;
        expandDiff({
          valueA: childA, valueB: childB, diffStatus: childStatus, path: childPath,
        });
      }
    }
  }

  const handledArrays: Set<string> = new Set();

  operations.forEach(op => {
    const path = extractActionFieldSegments(op.path);
    const diffStatus = readOp(op.op);

    if (!path || !diffStatus) return;

    // Array check to run the value-based diff
    const seg = path.split('/');
    const hit = findArrayAncestor(actionA ?? actionB, seg);
    if (hit) {
      // 1. Check for dedupe (one change has multiple operations)
      if (handledArrays.has(hit.arrayPath)) return;
      handledArrays.add(hit.arrayPath);

      // 2. Add the parent to diffField
      diffField[hit.arrayPath] = Operation.Modify;

      // 3. diff the arrays if they exist
      const initialArray = getAt(actionA, hit.arrayPath) ?? [];
      const currentArray = getAt(actionB, hit.arrayPath) ?? [];
      diffArrays(initialArray, currentArray, hit);
    } else {
      diffField[path] = diffStatus;

      // Object check to recursively pass in sub-objects
      const valueA = getAt(actionA, path);
      const valueB = getAt(actionB, path);
      expandDiff({
        valueA, valueB, diffStatus, path,
      });
    }
  });

  return diffField;
}
